// src/Services/roomCreationService.js
import { collection, doc, setDoc, deleteDoc, getDoc, updateDoc, query, where, getDocs, runTransaction, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";
import { getUserCountry, getCountryNameFromCode } from "./countryUtils";
import { statsService } from "./statisticsService";
import analytics from "./analyticsService";
import { MAX_OPEN_LOBBIES } from "./roomLimits";

// Excludes visually-ambiguous characters (0/O, 1/I/L) so a code read aloud
// or handwritten doesn't get mistyped.
const SHORT_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const SHORT_CODE_LENGTH = 6;

class RoomCreationService {
  // Generate unique room ID — internal Firestore doc ID, never shown to
  // users anymore (see generateShortCode for what they actually type/share).
  // Kept exactly as-is: existing rooms, deep links, and every other service
  // that queries by `roomId` depend on this format unchanged. Mirrors the
  // mobile app's services/createRoomService.ts 1:1.
  generateUniqueId() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  // Short, human-friendly join code shown to users instead of the long
  // internal roomId. A NEW field alongside roomId, not a replacement — old
  // rooms simply don't have one, and every join path still falls back to
  // roomId, so nothing that already shipped breaks.
  generateShortCode() {
    let code = "";
    for (let i = 0; i < SHORT_CODE_LENGTH; i++) {
      code += SHORT_CODE_ALPHABET[Math.floor(Math.random() * SHORT_CODE_ALPHABET.length)];
    }
    return code;
  }

  // Atomically claims a short code via a dedicated roomShortCodes/{code}
  // collection, keyed by the code itself. A plain "query for an existing
  // room with this code, then create the room" check is NOT atomic — two
  // concurrent creations can both see the code as free and both save a
  // room with it, so joining by that code can land on the wrong one. A
  // Firestore transaction reading and writing the SAME document (the
  // reservation doc) is: if two callers race on the same candidate, only
  // one transaction commits — the other is retried by the SDK, re-reads,
  // sees the doc now taken, and this loop tries a fresh candidate instead.
  // Mirrors mobile's services/createRoomService.ts 1:1.
  //
  // The reservation carries the same expiresAt as the room, so codes free
  // up again once a room expires (4h) rather than being claimed forever —
  // a stale reservation past its expiresAt is treated as available and
  // overwritten by whichever caller claims it next.
  async claimUniqueShortCode(roomId, expiresAt) {
    for (let attempt = 0; attempt < 10; attempt++) {
      const candidate = this.generateShortCode();
      const codeRef = doc(db, "roomShortCodes", candidate);
      try {
        const claimed = await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(codeRef);
          if (snap.exists()) {
            const data = snap.data();
            const reservedUntil = data.expiresAt?.toDate?.();
            if (reservedUntil && reservedUntil > new Date()) {
              return false; // still actively reserved by another room
            }
          }
          transaction.set(codeRef, { roomId, expiresAt: Timestamp.fromDate(expiresAt) });
          return true;
        });
        if (claimed) return candidate;
      } catch (error) {
        // Transaction aborted due to contention on this exact candidate —
        // someone else claimed it in the same instant. Try another.
        console.warn("roomCreationService: shortCode claim contention, retrying", error);
      }
    }
    // Every attempt failed to claim a code through the transaction above —
    // fail room creation instead of fabricating an unreserved code. An
    // unreserved code is never actually unique: it can silently collide
    // with another active room's code, and short-code lookups (which just
    // match the first room with that code) would then route joiners to
    // whichever room happens to be returned first, potentially the wrong
    // game entirely. 10 straight collisions against ~32^6 possible codes
    // means real contention or an outage, not bad luck. Mirrors mobile's
    // services/createRoomService.ts 1:1.
    throw new Error("Could not generate a unique room code. Please try again.");
  }

  // Get user's country (uses shared utility)
  async getUserCountry() {
    return getUserCountry();
  }

  // Convert country code to country name (uses shared utility)
  getCountryNameFromCode(countryCode) {
    return getCountryNameFromCode(countryCode);
  }
  validateRoomName(roomName, isPublic) {
    if (!isPublic) {
      return { isValid: true };
    }

    // Check length (30 characters max)
    if (roomName.length > 30) {
      return { 
        isValid: false, 
        message: 'Room name must be 30 characters or less' 
      };
    }

    // Basic profanity filter (expand this list as needed)
    const bannedWords = ['fuck', 'shit', 'damn', 'bitch', 'ass', 'hell'];
    const lowerRoomName = roomName.toLowerCase();
    
    for (const word of bannedWords) {
      if (lowerRoomName.includes(word)) {
        return { 
          isValid: false, 
          message: 'Please choose a different room name' 
        };
      }
    }

    return { isValid: true };
  }

  // Lobbies that are still waiting for the game to start. Creating one costs
  // nothing; only a game that starts uses the hosting allowance.
  async countOpenLobbies(userId) {
    const snapshot = await getDocs(
      query(collection(db, 'rooms'), where('hostId', '==', userId), where('gameStatus', '==', 'waiting'))
    );
    const now = Date.now();
    return snapshot.docs.filter((docSnap) => {
      const data = docSnap.data();
      if (data.isActive === false) return false;
      const expires = data.expiresAt?.toMillis ? data.expiresAt.toMillis() : data.expiresAt ? new Date(data.expiresAt).getTime() : 0;
      return !expires || expires > now;
    }).length;
  }

  async checkOpenLobbyCap(userId) {
    try {
      if ((await this.countOpenLobbies(userId)) >= MAX_OPEN_LOBBIES) {
        return {
          canCreate: false,
          reason: 'lobby_cap',
          message: `You already have ${MAX_OPEN_LOBBIES} lobbies open. Start or leave one first.`,
        };
      }
    } catch (error) {
      console.error('Error checking open lobbies:', error);
    }
    return { canCreate: true };
  }

  // Resolve maxPlayers: apply active session credit (consuming one session), or use passed value
  async resolveMaxPlayers(userId, passedMaxPlayers) {
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return passedMaxPlayers || 5;

      const credit = userSnap.data().roomUpgradeCredit;
      const now = new Date();

      if (
        credit &&
        credit.sessionsRemaining > 0 &&
        credit.expiresAt?.toDate?.() > now
      ) {
        const newRemaining = credit.sessionsRemaining - 1;
        if (newRemaining <= 0) {
          await updateDoc(userRef, { roomUpgradeCredit: null });
        } else {
          await updateDoc(userRef, { "roomUpgradeCredit.sessionsRemaining": newRemaining });
        }
        return credit.maxPlayers;
      }
    } catch (e) {
      console.error("Error resolving maxPlayers from credit:", e);
    }
    return passedMaxPlayers || 5;
  }

  // Create a new room
  async createRoom(playerName, options = {}) {
    if (!playerName.trim()) {
      throw new Error("Please enter your name first!");
    }

    try {
      // Get Firebase anonymous user ID
      const userId = await authService.getCurrentUserId();

      // DELETE_FLAG: Get user's country
      const country = await this.getUserCountry();

      // Generate room name
      const roomName = options.roomName || `${playerName}'s Room`;
      const isPublic = options.isPublic || false;

      // DELETE_FLAG: Validate room name for public rooms
      const nameValidation = this.validateRoomName(roomName, isPublic);
      if (!nameValidation.isValid) {
        return {
          success: false,
          message: nameValidation.message,
        };
      }

      const capCheck = await this.checkOpenLobbyCap(userId);
      if (!capCheck.canCreate) {
        return { success: false, reason: capCheck.reason, message: capCheck.message };
      }

      // Generate unique room ID
      const roomId = this.generateUniqueId();
      const currentTime = new Date();
      const expirationTime = new Date(currentTime);
      expirationTime.setHours(expirationTime.getHours() + 4);
      const shortCode = await this.claimUniqueShortCode(roomId, expirationTime);

      // Create room data with Firebase UID
      const roomData = {
        roomId,
        shortCode,
        roomName,
        hostId: userId,
        createdAt: currentTime,
        expiresAt: expirationTime,
        isActive: true,
        maxPlayers: await this.resolveMaxPlayers(userId, options.maxPlayers),
        players: [
          {
            id: userId,
            name: playerName,
            isHost: true,
            joinedAt: currentTime,
          },
        ],
        gameStatus: "waiting",
        currentRound: 0,
        playedLetters: [],
        enableSpecialRounds: options.enableSpecialRounds ?? false,
        categories: options.categories || ["name", "animal", "place", "thing"],
        useCustomCategories: options.useCustomCategories || false,
        isPublic, // DELETE_FLAG: Public visibility
        country, // DELETE_FLAG: Country for filtering
        // Power-up store fields
        pendingEffects: [],
        shields: {},
        roundItemUsage: {},
        consecutiveTargets: {},
      };

      // Save to Firebase with timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out")), 10000)
      );

      const roomRef = doc(db, "rooms", roomId);
      const savePromise = setDoc(roomRef, roomData);
      try {
        await Promise.race([savePromise, timeoutPromise]);
      } catch (raceError) {
        // Promise.race doesn't cancel the loser — if this was a timeout,
        // savePromise is still in flight and can still land after we've
        // already told the caller creation failed (who may then refund any
        // coins spent). If it does land, the room and its short-code
        // reservation would otherwise leak silently forever. Clean up in
        // the background rather than trusting the write to just vanish.
        if (raceError.message === "Request timed out") {
          savePromise
            .then(async () => {
              console.warn(
                `roomCreationService: write for room ${roomId} landed after the client had already timed out — deleting the orphaned room and freeing its short code`
              );
              try {
                await deleteDoc(roomRef);
                await deleteDoc(doc(db, "roomShortCodes", shortCode));
              } catch (cleanupError) {
                console.error("Failed to clean up orphaned room after a late write:", cleanupError);
              }
            })
            .catch(() => {}); // the write genuinely failed too — nothing to clean up
        }
        throw raceError;
      }

      // Track room creation in daily stats (fire-and-forget — never blocks the user)
      statsService.trackGameCreated(roomData).catch(() => {});

      analytics.track("room_created", {
        room_id: roomId,
        max_players: roomData.maxPlayers,
        special_rounds_enabled: roomData.enableSpecialRounds,
        is_public: isPublic,
        categories_source: roomData.useCustomCategories ? "custom" : "default",
      });

      // Create user document and add room
      await authService.createUserDocument(userId);
      await authService.addRoomToUser(roomId, {
        roomName: roomData.roomName,
        isHost: true,
      });

      // Store in localStorage (keep for compatibility during transition)
      localStorage.setItem("currentRoomId", roomId);
      localStorage.setItem("currentUserId", userId);
      localStorage.setItem("playerName", playerName);
      localStorage.setItem("isHost", "true");

      // Update hosted rooms history
      try {
        const existingHostedRooms = JSON.parse(
          localStorage.getItem("hostedRooms") || "[]"
        );
        const updatedHostedRooms = [
          ...existingHostedRooms,
          {
            roomId,
            roomName: roomData.roomName,
            createdAt: Date.now(),
            hostName: playerName,
            isPublic, // DELETE_FLAG: Track if room was public
          },
        ].slice(-5);

        localStorage.setItem("hostedRooms", JSON.stringify(updatedHostedRooms));
        localStorage.setItem("hostPlayerId", userId);
      } catch (localStorageError) {
        console.warn("LocalStorage update failed:", localStorageError);
      }

      return {
        success: true,
        roomId,
        roomData,
        message: isPublic ? 'Public room created successfully!' : 'Room created successfully!',
      };

    } catch (error) {
      console.error("Error creating room:", error);
      analytics.track("room_create_failed", {
        reason: error.code || error.message || "unknown",
        is_public: !!options.isPublic,
      });

      let errorMessage;
      if (error.message === "Request timed out") {
        errorMessage = "Request timed out. Please check your connection and try again.";
      } else if (error.code === "permission-denied") {
        errorMessage = "Permission denied. Please refresh the page and try again.";
      } else if (error.code === "unavailable") {
        errorMessage = "Service temporarily unavailable. Please try again in a moment.";
      } else {
        errorMessage = error.message || "Unable to create room right now. Please try again.";
      }

      return {
        success: false,
        error: error.code || 'unknown',
        message: errorMessage
      };
    }
  }

  // Quick create room (minimal options) - DELETE_FLAG: Updated to accept isPublic parameter
  async quickCreateRoom(playerName, isPublic = false) {
    return this.createRoom(playerName, { isPublic });
  }

  // Create room with custom settings
  async createCustomRoom(playerName, settings) {
    return this.createRoom(playerName, settings);
  }
}

// Export singleton instance
export const roomCreationService = new RoomCreationService();
export default roomCreationService;
